


import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Typography, Spin, message, Drawer, Divider, Tag, Progress, Row, Col } from "antd";
import {
  CheckCircleOutlined,
  MailOutlined,
  DownloadOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import courseService from "../../services/course.service";

const { Title, Paragraph, Text } = Typography;

const BadgeEligibility = ({ courseId, myNote, courseTitle, userId }) => {
  const badgeRef = useRef(null);

  const [threshold, setThreshold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Template du modérateur
  const [templateHtml, setTemplateHtml] = useState(null);
  const [templateSettings, setTemplateSettings] = useState(null);
  const [verificationCode, setVerificationCode] = useState(null);
  const [qrLink, setQrLink] = useState("https://nomidya.com");

  // Drawer notes détaillées
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [noteDetails, setNoteDetails] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const studentName = user?.username || "Étudiant";

  // Charger le threshold
  useEffect(() => {
    const fetchThreshold = async () => {
      try {
        const result = await courseService.getLastThresholdbadge(courseId);
        setThreshold(result?.threshold ?? 70);
      } catch (err) {
        console.error("Erreur threshold:", err);
      } finally {
        setLoading(false);
      }
    };
    if (courseId) fetchThreshold();
  }, [courseId]);

  // Charger le template badge + code de vérification
  useEffect(() => {
    const loadTemplateAndCode = async () => {
      if (!courseId) return;

      try {
        const template = await courseService.getLastBadgeTemplate(courseId);
        if (template) {
          setTemplateHtml(template.templateHtml || null);
          if (template.templateSettings) {
            try {
              setTemplateSettings(JSON.parse(template.templateSettings));
            } catch (e) {
              console.error("Erreur parsing settings:", e);
            }
          }
        }
      } catch (e) {
        console.log("Pas de template badge trouvé");
      }

      try {
        const code = await courseService.getBadgeVerificationCode(studentName, courseId);
        if (code) {
          setVerificationCode(code);
          setQrLink(`https://nomidya.com/badge/verify/${code}`);
        }
      } catch (e) {
        // Pas de badge existant
      }
    };

    loadTemplateAndCode();
  }, [courseId, studentName]);

  // Remplacer les variables dans le template HTML
  const processTemplate = (html) => {
    if (!html) return "";
    const today = new Date().toLocaleDateString("fr-FR");
    return html
      .replace(/\$\{studentName\}/g, studentName)
      .replace(/\{\{studentName\}\}/g, studentName)
      .replace(/\{\{username\}\}/g, studentName)
      .replace(/\$\{courseTitle\}/g, courseTitle || "")
      .replace(/\{\{courseTitle\}\}/g, courseTitle || "")
      .replace(/\{\{courseName\}\}/g, courseTitle || "")
      .replace(/\$\{score\}/g, String(myNote || 0))
      .replace(/\{\{score\}\}/g, String(myNote || 0))
      .replace(/\$\{threshold\}/g, String(threshold || 0))
      .replace(/\{\{threshold\}\}/g, String(threshold || 0))
      .replace(/\$\{today\}/g, today)
      .replace(/\{\{date\}\}/g, today);
  };

  // Télécharger le badge
  const handleDownloadBadge = async () => {
    if (!badgeRef.current) {
      message.error("Template non chargé.");
      return;
    }

    try {
      setDownloading(true);
      const canvas = await html2canvas(badgeRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
        allowTaint: true,
      });

      const link = document.createElement("a");
      link.download = `badge_${studentName}_${courseTitle || "cours"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      message.success("🎉 Badge téléchargé !");
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      message.error("Erreur lors du téléchargement.");
    } finally {
      setDownloading(false);
    }
  };

  // Envoyer par email
  const handleSendToEmail = async () => {
    try {
      setSendingEmail(true);
      const result = await courseService.sendBadgeToEmail(courseId, userId);

      if (result.status === "SUCCESS") {
        message.success(`📧 Badge envoyé à ${result.email}`);
      } else {
        message.warning(result.message || "Impossible d'envoyer.");
      }
    } catch (error) {
      console.error("Erreur envoi email:", error);
      message.error(error.response?.data?.message || "Erreur envoi email.");
    } finally {
      setSendingEmail(false);
    }
  };

  // Ouvrir le drawer avec les notes détaillées
  const handleShowNotes = async () => {
    setDrawerVisible(true);
    if (noteDetails) return;

    setLoadingNotes(true);
    try {
      const result = await courseService.CalculeNoteFinale(courseId, userId);
      if (Array.isArray(result) && result.length > 0) {
        setNoteDetails(result[0]);
      }
    } catch (err) {
      console.error("Erreur chargement notes:", err);
    } finally {
      setLoadingNotes(false);
    }
  };

  if (loading) {
    return <Spin tip="Chargement du seuil..." />;
  }

  // Settings du template
  const s = templateSettings || {};
  const bgColor = s.backgroundColor || "#1890ff";
  const txtColor = s.textColor || "#ffffff";
  const bWidth = s.badgeWidth || 300;
  const bHeight = s.badgeHeight || 420;
  const logoW = s.logoWidth || 60;
  const logoH = s.logoHeight || 60;
  const useImgBg = s.useImageBackground || false;
  const bgImg = s.backgroundPreview || null;
  const logoImg = s.logoPreview || null;

  const processedHtml = processTemplate(templateHtml);

  const defaultBadgeHtml = `
    <div style="text-align: center; height: 100%; padding: 20px; display: flex; flex-direction: column; justify-content: center;">
      <div>
        <h2 style="margin: 10px 0; font-size: 18px; font-weight: bold;">🏆 BADGE DE RÉUSSITE</h2>
        <div style="margin: 20px 0;">
          <div style="font-size: 14px; margin-bottom: 8px;">Décerné à :</div>
          <div style="font-size: 16px; font-weight: bold; text-transform: uppercase; border-bottom: 2px solid currentColor; padding-bottom: 5px; margin: 0 20px;">
            ${studentName}
          </div>
        </div>
        <div style="margin: 20px 0;">
          <div style="font-size: 12px; margin-bottom: 5px;">Pour le cours :</div>
          <div style="font-size: 13px; font-weight: 600; font-style: italic; padding: 0 10px;">
            ${courseTitle || "Cours"}
          </div>
        </div>
        <div style="margin: 20px 0;">
          <div style="font-size: 24px; margin-bottom: 5px;">⭐</div>
          <div style="font-size: 12px;">Score : ${myNote?.toFixed?.(2) || myNote} / ${threshold}</div>
        </div>
      </div>
    </div>
  `;

  const nd = noteDetails;
  const passed = myNote != null && threshold != null && myNote >= threshold;

  return (
    <>
      {myNote > threshold ? (
        <div style={{
          marginTop: 24, padding: "2rem",
          background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 10,
        }}>
          <Title level={4} style={{ color: "#389e0d" }}>🎓 Félicitations !</Title>
          <Paragraph style={{ fontSize: 16 }}>
            Vous êtes éligible pour recevoir votre Badge.
          </Paragraph>

          <Card bordered={false} style={{
            backgroundColor: "#ffffff", border: "1px solid #d9d9d9",
            borderRadius: 12, boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
            marginTop: 16, textAlign: "center", padding: "24px 16px",
          }}>
            <Paragraph style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
              👤 <strong style={{ color: "#1890ff" }}>{studentName}</strong> — 
              📘 <strong style={{ color: "#52c41a" }}>{courseTitle}</strong> — 
              📊 Note : <strong>{myNote?.toFixed?.(2) || myNote}</strong> / {threshold}
            </Paragraph>

            {/* Badge rendu depuis le template */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
              <div
                ref={badgeRef}
                style={{
                  width: `${bWidth}px`,
                  height: `${bHeight}px`,
                  background: useImgBg && bgImg
                    ? `url(${bgImg}) center/cover no-repeat`
                    : bgColor,
                  color: txtColor,
                  borderRadius: "16px",
                  position: "relative",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                }}
              >
                {/* Logo */}
                {logoImg && (
                  <img
                    src={logoImg}
                    alt="Logo"
                    style={{
                      position: "absolute", top: "20px", left: "50%",
                      transform: "translateX(-50%)",
                      width: `${logoW}px`, height: `${logoH}px`,
                      borderRadius: "50%", objectFit: "cover",
                    }}
                  />
                )}

                {/* Contenu HTML — padding-bottom pour ne pas chevaucher le QR */}
                <div style={{ padding: "20px", paddingBottom: "80px", height: "100%", position: "relative" }}>
                  <div dangerouslySetInnerHTML={{
                    __html: processedHtml || defaultBadgeHtml
                  }} />
                </div>

                {/* QR Code — en bas à droite avec fond semi-transparent */}
                <div style={{
                  position: "absolute", bottom: "12px", right: "12px",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "8px", padding: "6px",
                }}>
                  <QRCodeCanvas
                    value={qrLink}
                    size={52}
                    bgColor="transparent"
                    fgColor={txtColor}
                  />
                </div>

                {/* Date en bas à gauche */}
                <div style={{
                  position: "absolute", bottom: "14px", left: "14px",
                  fontSize: "10px", opacity: 0.8, color: txtColor,
                }}>
                  {new Date().toLocaleDateString("fr-FR")}
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                type="primary" size="large" icon={<DownloadOutlined />}
                loading={downloading} onClick={handleDownloadBadge}
                style={{
                  borderRadius: "10px", fontWeight: 600, height: "46px",
                  background: "linear-gradient(135deg, #52c41a, #389e0d)",
                  border: "none", boxShadow: "0 4px 12px rgba(82, 196, 26, 0.3)",
                }}
              >
                Télécharger
              </Button>

              <Button
                size="large" icon={<MailOutlined />}
                loading={sendingEmail} onClick={handleSendToEmail}
                style={{
                  borderRadius: "10px", fontWeight: 600, height: "46px",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none", color: "white",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                Envoyer par email
              </Button>

              <Button
                size="large" icon={<BarChartOutlined />} onClick={handleShowNotes}
                style={{
                  borderRadius: "10px", fontWeight: 600, height: "46px",
                  background: "linear-gradient(135deg, #faad14, #d48806)",
                  border: "none", color: "white",
                  boxShadow: "0 4px 12px rgba(250, 173, 20, 0.3)",
                }}
              >
                Mes notes
              </Button>
            </div>

            {/* Lien de vérification */}
            {verificationCode && (
              <div style={{ marginTop: 16, fontSize: 12, color: "#999" }}>
                🔗 Vérification :{" "}
                <a href={qrLink} target="_blank" rel="noopener noreferrer" style={{ color: "#667eea" }}>
                  {qrLink}
                </a>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <div style={{
          marginTop: 24, padding: "1.5rem",
          background: "#fff2f0", border: "1px solid #ffccc7",
          borderRadius: 10, color: "#cf1322", fontWeight: "500",
        }}>
          ⚠️ Vous n'avez pas encore le droit d'obtenir votre Badge,
          car votre note (<strong>{myNote}</strong>) ne dépasse pas le seuil requis de {threshold}.

          <div style={{ marginTop: 12 }}>
            <Button
              size="small" icon={<BarChartOutlined />} onClick={handleShowNotes}
              style={{ borderRadius: "8px", fontWeight: 500 }}
            >
              Voir mes notes détaillées
            </Button>
          </div>
        </div>
      )}

      {/* DRAWER : Détails des notes */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <TrophyOutlined style={{ fontSize: 20, color: "#faad14" }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Détails de mes notes</span>
          </div>
        }
        placement="right"
        width={400}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {loadingNotes ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: "#999" }}>Chargement des notes...</p>
          </div>
        ) : nd ? (
          <div>
            {/* Note finale circulaire */}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Progress
                type="circle"
                percent={Math.min(Math.round((nd.noteFinale || 0)), 100)}
                format={() => (
                  <div>
                    <div style={{ fontSize: 28, fontWeight: "bold", color: passed ? "#52c41a" : "#ff4d4f" }}>
                      {nd.noteFinale?.toFixed(2) || "0.00"}
                    </div>
                    <div style={{ fontSize: 12, color: "#999" }}>/ 100</div>
                  </div>
                )}
                strokeColor={passed ? "#52c41a" : "#ff4d4f"}
                width={140}
              />
              <div style={{ marginTop: 10 }}>
                <Tag color={passed ? "success" : "error"} style={{ fontSize: 13, padding: "4px 16px" }}>
                  {passed ? "✅ Seuil atteint" : "❌ Seuil non atteint"}
                </Tag>
              </div>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Seuil requis : <Tag color="blue">{threshold}</Tag>
                </Text>
              </div>
            </div>

            <Divider>Détail par type</Divider>

            {/* Notes par catégorie */}
            <Row gutter={[12, 12]}>
              {nd.noteControle != null && (
                <Col span={24}>
                  <Card size="small" style={{ borderRadius: 10, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 13, color: "#666" }}>📝 Contrôle de connaissances</div>
                      <div style={{ fontSize: 24, fontWeight: "bold", color: "#52c41a" }}>
                        {nd.noteControle.toFixed(2)}
                      </div>
                    </div>
                  </Card>
                </Col>
              )}
              {nd.noteEvaluation != null && (
                <Col span={24}>
                  <Card size="small" style={{ borderRadius: 10, background: "#e6f7ff", border: "1px solid #91d5ff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 13, color: "#666" }}>📋 Évaluation</div>
                      <div style={{ fontSize: 24, fontWeight: "bold", color: "#1890ff" }}>
                        {nd.noteEvaluation.toFixed(2)}
                      </div>
                    </div>
                  </Card>
                </Col>
              )}
              {nd.noteTraining != null && (
                <Col span={24}>
                  <Card size="small" style={{ borderRadius: 10, background: "#fff7e6", border: "1px solid #ffd591" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 13, color: "#666" }}>🏋️ Entraînement</div>
                      <div style={{ fontSize: 24, fontWeight: "bold", color: "#fa8c16" }}>
                        {nd.noteTraining.toFixed(2)}
                      </div>
                    </div>
                  </Card>
                </Col>
              )}
            </Row>

            <Divider />

            <div style={{
              padding: 12, background: "#f8f9fa", borderRadius: 8,
              fontSize: 12, color: "#666",
            }}>
              <InfoCircleOutlined style={{ marginRight: 6, color: "#1890ff" }} />
              La note finale est calculée à partir de vos résultats aux contrôles,
              évaluations et entraînements, pondérés selon les pourcentages définis par le formateur.
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
            <BarChartOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
            <p>Aucune note disponible pour le moment.</p>
            <p style={{ fontSize: 12 }}>Complétez les quiz et évaluations du cours.</p>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default BadgeEligibility;